import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {AsyncDataLoader, AsyncDataLoaderProps} from '../../../../components/common/loaders/asyncDataLoader';
import {EditorSection} from '../../../editors/commons/editorSection';
import {useConditionCardProps, useConditionGroups} from '../../../editors/conditions/conditionsHelpers';
import {ConditionsList} from '../../../editors/conditions/conditionsList';
import {previewerEditorConditionGroups} from '../../../editors/previewerEditor/previewerConditionsHelpers';
import {PreviewerEditorStateActions} from '../../../editors/previewerEditor/usePreviewerEditorState';
import {ClientRuleCondition} from '../../../rules/ruleConditionModel';
import {RuleIntegration, useRuleIntegrations} from '../../../rules/ruleIntegrationModel';

/*
 * Props.
 */

interface PreviewerConditionsSectionProps {
  conditions: ReadonlyArray<ReadonlyArray<ClientRuleCondition>>;

  onAddCondition: PreviewerEditorStateActions['onAddCondition'];
  onUpdateCondition: PreviewerEditorStateActions['onUpdateCondition'];
  onDeleteCondition: PreviewerEditorStateActions['onDeleteCondition'];

  ruleIntegration: RuleIntegration;
}

/*
 * Style.
 */

const StyledConditionsWrapperDiv = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 5px;
`;

// Temporary hack to avoid the conditions loader to take 100% height.
// As `styled-components` is not preserving the generic from `AsyncDataLoader`,
// we need to use FC<AsyncDataLoaderProps<T>>.
// See https://github.com/styled-components/styled-components/issues/1803#issuecomment-719953583.
const StyledAsyncDataLoader = styled(AsyncDataLoader)`
  max-height: 120px;
`;

/*
 * Component.
 */

const PreviewerConditionsSection: FC<PreviewerConditionsSectionProps> = (props) => {
  const {
    conditions: andConditions,
    onAddCondition,
    onUpdateCondition,
    onDeleteCondition,
    ruleIntegration,
  } = props;

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle(andConditions.length === 0);

  const maybeRenderMoreOptionsButton = () => {
    // Don't display the button if we don't have any condition.
    if (andConditions.length === 0) {
      return null;
    }

    return (
      <Button
        variant={ButtonVariantsEnum.LINK}
        size={VisualSizesEnum.SMALL}
        color={PaletteColorsEnum.GREY}
        onClick={toggleMoreOptions}
      >
        {shouldShowMoreOptions ? (
          <Trans id="previewer-editor-conditions-fewer-options">Fewer options</Trans>
        ) : (
          <Trans id="previewer-editor-conditions-more-options">More options</Trans>
        )}
      </Button>
    );
  };

  const conditionsProps = useConditionCardProps(
    andConditions,
    (condition: ClientRuleCondition) => ruleIntegration.findConditionDefinition(condition),
    onUpdateCondition,
    onDeleteCondition,
    noop,
  );
  const previewerConditionGroups = useConditionGroups(ruleIntegration, previewerEditorConditionGroups);

  return (
    <EditorSection
      description={<Trans id="previewer-editor-conditions">Conditions</Trans>}
      renderRight={maybeRenderMoreOptionsButton}
    >
      <StyledConditionsWrapperDiv>
        <ConditionsList
          isAdvancedEditor={shouldShowMoreOptions}
          conditions={conditionsProps}
          conditionGroups={previewerConditionGroups}
          onAddCondition={onAddCondition}
        />
      </StyledConditionsWrapperDiv>
    </EditorSection>
  );
};

// @TODO: Move upper to avoid fetching integrations every time we're mounting the editor.
export const PreviewerConditionsSectionWithIntegration = (
  props: Omit<PreviewerConditionsSectionProps, 'ruleIntegration'>,
) => {
  const ruleIntegrationsAsyncData = useRuleIntegrations();

  return (
    <StyledAsyncDataLoader<FC<AsyncDataLoaderProps<RuleIntegration>>> data={ruleIntegrationsAsyncData}>
      {(ruleIntegration) => <PreviewerConditionsSection {...props} ruleIntegration={ruleIntegration} />}
    </StyledAsyncDataLoader>
  );
};
